import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetIdentity, useLogout } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { Layout, Badge, Input, Breadcrumb, message, Avatar, Dropdown } from "antd";
import {
  BellOutlined,
  SunOutlined,
  MoonOutlined,
  SearchOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import axios from "axios";
import { API_URL } from "../../../config";

const { Header: AntdHeader } = Layout;

const routeLabels: Record<string, string> = {
  dashboard: "Dashboard",
  attendance: "Attendance",
  tasks: "Tasks",
  curriculum: "Curriculum",
  presentations: "Presentations",
  tickets: "Tickets",
  settings: "Settings",
};

interface HeaderProps {
  collapsed: boolean;
  setCollapsed: (val: boolean) => void;
  onOpenMobileDrawer?: () => void;
}

const Header: React.FC<HeaderProps> = ({ collapsed, setCollapsed, onOpenMobileDrawer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { data: user } = useGetIdentity();
  const { mutate: logoutMutation } = useLogout();

  const getBreadcrumbItems = () => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const items = [
      {
        key: "home",
        title: (
          <span
            onClick={() => navigate("/tutor/dashboard")}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
          >
            <HomeOutlined /> Home
          </span>
        ),
      },
    ];

    let currentPath = "";
    pathParts.forEach((part, index) => {
      if (part === "tutor") return;
      currentPath += `/${part}`;
      const label = routeLabels[part] || part.charAt(0).toUpperCase() + part.slice(1);
      items.push({
        key: part,
        title:
          index === pathParts.length - 1 ? (
            <span>{label}</span>
          ) : (
            <span
              onClick={() => navigate(`/tutor${currentPath}`)}
              style={{ cursor: "pointer" }}
            >
              {label}
            </span>
          ),
      });
    });

    return items;
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (token) {
        await axios.post(
          `${API_URL}/api/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userData");
      delete axios.defaults.headers.common["Authorization"];
      logoutMutation();
      message.success("Logged out successfully");
      navigate("/team");
    } catch (error) {
      localStorage.clear();
      delete axios.defaults.headers.common["Authorization"];
      message.success("Logged out successfully");
      navigate("/team");
    }
  };

  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const parsed = JSON.parse(userData);
        return `${parsed.firstName || ""} ${parsed.lastName || ""}`.trim() || "Tutor";
      }
    } catch (e) {
      return "Tutor";
    }
    return "Tutor";
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
      onClick: () => navigate("/tutor/profile"),
    },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "Settings",
      onClick: () => navigate("/tutor/settings"),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log out",
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <AntdHeader className="admin-header">
      <div className="admin-header-left">
        <button
          type="button"
          onClick={onOpenMobileDrawer}
          className="md:hidden flex items-center justify-center w-9 h-9 rounded-md text-foreground transition-colors hover:bg-muted"
          aria-label="Open menu"
        >
          <MenuOutlined style={{ fontSize: 18 }} />
        </button>

        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{
            cursor: "pointer",
            fontSize: "18px",
            color: "var(--dyen-text)",
            padding: "4px",
            borderRadius: "4px",
            transition: "background 0.2s",
            marginRight: "8px",
          }}
          className="sidebar-toggle-btn hidden md:flex items-center"
          title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>

        <Breadcrumb className="admin-breadcrumb hidden sm:flex" items={getBreadcrumbItems()} />
      </div>

      <div className="admin-header-right">
        <Input
          className="header-search hidden md:flex"
          placeholder="Search..."
          prefix={<SearchOutlined />}
          allowClear
        />

        <button
          className="theme-toggle"
          onClick={toggleTheme}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? <SunOutlined /> : <MoonOutlined />}
        </button>

        <Badge count={3} size="small">
          <button
            className="notification-btn"
            onClick={() => message.info("Notifications feature coming soon")}
          >
            <BellOutlined />
          </button>
        </Badge>

        <Dropdown menu={{ items: userMenuItems }} trigger={["click"]} placement="bottomRight">
          <button
            className="hidden sm:flex items-center gap-2 px-2 py-1 rounded-md transition-colors hover:bg-muted"
            type="button"
          >
            <Avatar
              size={28}
              style={{ backgroundColor: "#FFC200", color: "#000" }}
              icon={<UserOutlined />}
            />
            <span className="text-sm text-foreground">{getUserDisplayName()}</span>
          </button>
        </Dropdown>
      </div>
    </AntdHeader>
  );
};

export default Header;
